<template>
    <v-layout row>
        <v-flex xs12 sm12>
            <v-card class="elavation-1 transparent">
                <v-toolbar class="transparent" flat height="37">
                    <v-btn dark icon title="Back to Settings" @click="$router.push('/settings')">
                        <v-icon>arrow_back</v-icon>
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn v-if="hasEditRights" dark fab color="primary" small title="Add Team Role" @click="addTeamRole">
                        <v-icon dark>add</v-icon>
                    </v-btn>
                </v-toolbar>
            </v-card>
            
            <v-alert :value="true" color="info" icon="info" outline dismissible class="mt-8">
                With <b>Team Membership Roles and Permissions</b>, you can manage each employee’s access to My Teams pages based on their individual team memberships. You can use the predefined system roles provided or create custom roles tailored to your specific needs.
            </v-alert> 

            <v-card class="elavation-1 transparent mt-5">
                <v-toolbar flat height="45px">
                    <div :class="isMobile?'wp-60 mb-10':'wp-30 mb-10'">
                        <v-text-field v-model="search" append-icon="search" label="Search name or description" single-line hide-details></v-text-field>
                    </div>
                    <v-spacer></v-spacer>
                </v-toolbar>
                <v-data-table
                    :headers="isMobile?header_mobile:header"
                    :items="team_roles_filtered"
                    :loading="loading"
                    :rows-per-page-items="rowsPerPageItems"
                    :pagination.sync="paginationSync"
                >
                    <v-progress-linear v-slot:progress color="primary" indeterminate></v-progress-linear>
                    <template v-slot:items="props">
                        <tr @click="viewDetails(props.item.teamRoleId)" title="Click to view permissions" class="clickable">
                            <td :title="props.item.description">
                                <v-chip class="user-chip-container">
                                    {{ props.item.name }}
                                </v-chip>
                            </td>
                            <td>
                                <span v-if="props.item.visibleInMyTeams">Yes</span>
                                <span v-else>No</span>
                            </td>
                            <td v-if="!isMobile">
                                <span v-if="props.item.isSystemRole">System Predefined</span>
                                <span v-else>User Defined</span>
                            </td>
                            <td v-if="!isMobile">
                                <span v-if="props.item.isSystemRole">System</span>
                                <v-chip v-else class="user-chip-container" small>
                                    <v-avatar>
                                        <v-img :src="awsS3Helper.getAvatarUrl(props.item.createdByUser.userId)"></v-img>
                                    </v-avatar>
                                    <b>{{props.item.createdByUser.firstName}} {{props.item.createdByUser.lastName}}</b>
                                </v-chip>
                            </td>
                            <td v-if="!isMobile">{{ props.item.createdOn | moment('MMM DD, YYYY') }}</td>
                        </tr>
                    </template>
                </v-data-table> 
            </v-card>
        </v-flex>

        <v-dialog v-model="dialogAddEdit" persistent width="600">
            <add-edit ref="addEditRole" :dialogAddEdit.sync="dialogAddEdit" @send="viewDetails"></add-edit>
        </v-dialog>
    </v-layout> 
</template>

<script>
import userPrefs from '@/helpers/user-prefs'
import rowsPerPageItems from '@/constants/rows-per-page'
import teamRolesApi from '@/api/teamroles'
import awsS3Helper from '@/helpers/aws-s3'
import { hasEditRights } from '@/helpers/access-permissions'

export default {     
    name: 'TeamRoles',
    metaInfo: {
        title: 'Team Roles',
        meta: [
            { charset: 'utf-8' },
            { name: 'description', content: 'Team Roles' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' }
            ]
    },
    components: {
        'add-edit': () => import('@/components/TeamRoles/AddEditTeamRole'),
    },   
    data () {
        const routeName = this.$route.name
        const savedSort = userPrefs.getSort(routeName)

        return {
            awsS3Helper,
            rowsPerPageItems,
            paginationSync: {
                rowsPerPage: userPrefs.getRowsPerPage(routeName),
                sortBy: savedSort.sortBy,
                descending: savedSort.descending,
            },
            search: userPrefs.getSearchKeyword(routeName),
            error: false,
            errorMessage: '',
            loading: false,
            dialogAddEdit: false,
            header: [
                {text:'Name', align:'left', value:'name', width: '15%', sortable:true},
                {text:'Visible In My Teams', align:'left', value: 'visibleInMyTeams', width: '10%', sortable:true},
                {text:'Type', align:'left', value: 'isSystemRole', width: '15%', sortable:true},
                {text:'Created By', align:'left', value: 'lastName', width:'10%', sortable:true},
                {text:'Created On', align: 'left', value:'gracePeriod', width: '10%', sortable:false},
            ],
            header_mobile: [
                {text:'Name', align:'left', value:'name', width: '15%', sortable:true},
                {text:'Visible In My Teams', align:'left', value: 'visibleInMyTeams', width: '10%', sortable:true},
            ],
            team_roles: []
        }
    },
    methods: {
        addTeamRole() {
            this.$refs.addEditRole.setAdd(this.team_roles)
            this.dialogAddEdit = true
        },
        viewDetails(teamRoleId) {
            this.$router.push(`/settings/teamroles/${teamRoleId}`)
        },
        async getRoles(show_loading) {
            try {
                this.loading = show_loading
                const response = await teamRolesApi.getRoles() 
                if (response && response.status == 200) {
                    this.team_roles = response.data.items
                }
                this.loading = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
    },
    async created() {
        //update header titles
        this.$root.$emit('setHeaderTitles', {
            display: true,
            headerTitle: 'Team Membership Roles',
            headerPageTitle: ''
        })

        this.getRoles(true)
    },
    watch: {
        'paginationSync.rowsPerPage'(newVal) {
            userPrefs.updateRowsPerPage(this.$route.name, newVal)
        },
        'paginationSync.sortBy'(newVal) {
            userPrefs.updateSort(this.$route.name, newVal, this.paginationSync.descending)
        },
        'paginationSync.descending'(newVal) {
            userPrefs.updateSort(this.$route.name, this.paginationSync.sortBy, newVal)
        },
        search(newVal) {
            userPrefs.updateSearchKeyword(this.$route.name, newVal)
        }
    },
    computed: {
        hasEditRights() {
            return hasEditRights(this.$router.currentRoute.path)
        },
        team_roles_filtered() {
            var temp = this.team_roles

            //search handler
            if (this.search) {
                const searchLower = this.search.toLowerCase()
                temp = temp.filter(item => {
                    return item.name.toLowerCase().includes(searchLower) ||
                            item.description.toLowerCase().includes(searchLower)
                            
                })
            }
            return temp
        },
        isMobile() {
            return window.innerWidth < 600
        },
    }
}
</script>
